import {
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { OnboardingLayout } from 'components/OnboardingLayout';
import { useOnboardingState } from 'state/useOnboardingState';
import { useCriticalAlertsOnboarding } from 'hooks/useCriticalAlertsOnboarding';
import { TextLogo } from 'components/TextLogo';
import { TERMS_OF_SERVICE, PRIVACY_POLICY } from '../../../../constants';

const useStyles = makeStyles()((theme) => ({
  list: {
    listStyleType: 'disc',
  },
  listItem: {
    display: 'list-item',
    marginLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  textLogo: {
    // hard-coding to prevent iOS Dynamic Type from resizing this
    width: 200, // 9rem
  },
}));

export const WelcomeContent = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { notificationsEnabled } = useOnboardingState();
  const { shouldShowCriticalAlertsStep } = useCriticalAlertsOnboarding();

  const handleGetStarted = (): void => {
    const redirectToCriticalAlerts =
      shouldShowCriticalAlertsStep(notificationsEnabled);
    // Check if we need to show critical alerts permission first
    if (redirectToCriticalAlerts) {
      history.push('/critical_alerts_permission');
      return;
    }

    if (notificationsEnabled) {
      history.push('/onboarding_notifications');
      return;
    }
    history.push('/permissions');
  };

  return (
    <OnboardingLayout
      title={<TextLogo className={classes.textLogo} />}
      footerText={
        <Stack spacing={1}>
          <Typography>{t('welcome.footer')}</Typography>

          <Typography variant="body2">
            <Trans i18nKey="welcome.tos">
              By clicking <b>Continue</b>, you are agreeing to our
              <Link
                fontWeight="fontWeightBold"
                target="blank"
                href={TERMS_OF_SERVICE}
                underline="always"
                color="textPrimary"
              >
                Terms of Service
              </Link>
              and
              <Link
                fontWeight="fontWeightBold"
                target="blank"
                href={PRIVACY_POLICY}
                underline="always"
                color="textPrimary"
              >
                Privacy Policy
              </Link>
            </Trans>
          </Typography>
        </Stack>
      }
      buttonTitle={t('welcome.button')}
      onClick={handleGetStarted}
    >
      <Stack spacing={3}>
        <Typography variant="h3" fontWeight="fontWeightRegular">
          <Trans i18nKey="welcome.description">
            We are a <b>nonprofit</b> providing real-time alerting and
            monitoring of wildfires across the United States.
          </Trans>
        </Typography>

        <Stack spacing={0}>
          <Typography variant="h3">{t('welcome.howItWorks.title')}</Typography>

          <List dense className={classes.list}>
            <ListItem
              disableGutters
              className={classes.listItem}
              disablePadding
            >
              <ListItemText
                primary={t('welcome.howItWorks.item1')}
                slotProps={{
                  primary: { variant: 'body1' },
                }}
              />
            </ListItem>
            <ListItem disableGutters className={classes.listItem}>
              <ListItemText
                primary={t('welcome.howItWorks.item2')}
                slotProps={{
                  primary: { variant: 'body1' },
                }}
              />
            </ListItem>
            <ListItem disableGutters className={classes.listItem}>
              <ListItemText
                primary={t('welcome.howItWorks.item3')}
                slotProps={{
                  primary: { variant: 'body1' },
                }}
              />
            </ListItem>
          </List>
        </Stack>
      </Stack>
    </OnboardingLayout>
  );
};
